/* eslint-disable react/jsx-no-useless-fragment */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useMemo
} from 'react';
import { Button, Menu, Skeleton, Tooltip } from 'antd';
import PageSortPanel from '@components/PageSortPanel';
import { useCurrentWorkspace, useDefaultWorkspace } from '@hooks/workspace';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ReactComponent as DeletedSvg } from '@assets/icons/deleted-tasks-head-icon.svg';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import { useTypedSelector } from '@hooks';
import { useDispatch } from 'react-redux';
import { CampaignsTable } from '@components/Tables';
import {
  toggleCampaignFolderCreationModal,
  toggleCampaignInfoModal
} from '@redux/actions/modalAction';
import CampaignCard from '@components/CampaignCard';
import { useLayout } from '@layouts/PageLayout';
import { useWebSocketDebouncedSubscription } from '@hooks/useWebSocketClient';
import LottieComponent from '@components/Lottie';
import { DraggableItem } from '@components/DraggableList';
import { DraggableList } from '@components/DraggableList/DraggableList';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { useAuth } from '@hooks/useAuth';
import { NEW_ONBOARDING_RELEASE_DATE } from '@config/constants';
import { useDebounced } from '@helpers/useDebounced';
import { useQueryClient } from '@tanstack/react-query';
import {
  CampaignControllerListCampaingsParams,
  CampaignItemDto
} from '@api/Api';
import { changeCampaignOrder } from '@api/Campaign';
import {
  useCampaignsCountQuery,
  useCampaignsQuery
} from '@hooks/queries/campaigns';
import { optimisticListItemReorder } from '@hooks/queries/helpers';
import { useOrganization } from '@components/OrganizationBoundary';

function CampaignsListEmptyState({
  canCreateCampaign = true,
  hasTourToCreateCampaign = false
}) {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const startTour = searchParams.get('startTour') ?? undefined;
  const { onStartTour, tourData } = useOnboardingProcess();

  useEffect(() => {
    if (!hasTourToCreateCampaign) return;
    onStartTour({
      type: startTour === 'welcome-modal' ? 'welcome-modal' : 'empty-campaigns'
    });
  }, [hasTourToCreateCampaign, startTour, onStartTour]);

  const onCreateProject = useCallback(() => {
    if (!canCreateCampaign) return;
    const hasActiveCampaignsTour = tourData?.type === 'empty-campaigns';
    if (hasActiveCampaignsTour) onStartTour({ type: undefined });
    dispatch(
      toggleCampaignFolderCreationModal({
        visible: true,
        entity: 'campaign',
        createFromOnboarding: hasActiveCampaignsTour
      })
    );
  }, [dispatch, onStartTour, tourData, canCreateCampaign]);

  return (
    <div className="empty_state">
      <div className="empty_state-content tour-campaign-empty-state">
        <LottieComponent className="campaign-icon" view="addCampaign" />
        <div className="title">
          {hasTourToCreateCampaign ? (
            <>
              You haven’t created <br /> any projects yet - try it out!
            </>
          ) : (
            'There are no projects yet'
          )}
        </div>
        <div className="copy">Create your first project</div>
        <Tooltip
          overlayClassName="long-text-hint"
          overlayInnerStyle={{ maxWidth: 285, textAlign: 'left' }}
          title={
            !canCreateCampaign &&
            'Your current role in StreamWork doesn’t allow you to create a project.'
          }
          trigger="hover"
          placement="bottom"
        >
          <Button
            type="primary"
            disabled={!canCreateCampaign}
            onClick={onCreateProject}
          >
            New project
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

const CampaignsListSkeleton = forwardRef<HTMLDivElement>(
  function CampaignsListSkeleton(props, ref) {
    return (
      <div ref={ref} className="campaign_card_container">
        <>
          <div className="assets assets_0">
            <Skeleton.Input style={{ width: '100%', height: 136 }} />
          </div>
          <div className="campaign_info">
            <Skeleton.Input style={{ width: '100%', height: 42 }} />
          </div>
        </>
      </div>
    );
  }
);

function modifyParams(
  page: 'all' | 'my' | 'favorite' | 'deleted',
  workspaceId: string
) {
  let params: any = {
    workspaceId
  };
  if (page === 'my')
    params = {
      ...params,
      own: true
    };

  if (page === 'favorite')
    params = {
      ...params,
      isFavorite: true
    };
  if (page === 'deleted')
    params = {
      ...params,
      isDeleted: true
    };

  return params;
}

const draggableOrder = 'order:DESC';
const defaultOrder = draggableOrder;
const orderByKey = 'campaigns-order-by';

function CampaignsListInWorkspace({
  page
}: {
  page: 'all' | 'my' | 'favorite' | 'deleted';
}) {
  const { currentView } = useLayout();
  const { user } = useAuth();
  const urlParams = useParams();
  const dispatch = useDispatch();
  const { currentOrganization } = useOrganization();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const campaignInfoModal = useTypedSelector(
    ({ modal }) => modal.campaignInfoModal
  );
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<string>(
    () => localStorage.getItem(orderByKey) || defaultOrder
  );
  const debouncedSearchQuery = useDebounced(searchQuery, 250);

  const hasTourToCreateCampaign = useMemo(() => {
    if (currentOrganization.entity?.features.disableOnboarding) return false;
    if (!currentWorkspace?.permissions?.createCampaigns) return false;
    if (currentWorkspace?.role === 'workspace://contributor') return false;
    const isNewUserForOnboarding =
      user?.createdAt &&
      new Date(user.createdAt).getTime() >=
        NEW_ONBOARDING_RELEASE_DATE.getTime();
    if (
      !user?.completedOnboardingSteps?.includes('create-campaign') &&
      isNewUserForOnboarding
    )
      return true;
    return false;
  }, [
    currentOrganization.entity?.features.disableOnboarding,
    user?.completedOnboardingSteps,
    user?.createdAt,
    currentWorkspace?.permissions?.createCampaigns,
    currentWorkspace?.role
  ]);

  const prevPage = useRef(page);
  const queryClient = useQueryClient();
  const campaignsQuery = useCampaignsQuery(
    {
      ...modifyParams(page, urlParams.workspaceId as string),
      searchQuery: debouncedSearchQuery,
      orderBy: [orderBy]
    },
    {
      keepPreviousData: page === prevPage.current,
      enabled: !hasTourToCreateCampaign
    }
  );
  const isLoading = campaignsQuery.isLoading && !hasTourToCreateCampaign;
  const campaignsList = useMemo(() => {
    return campaignsQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [campaignsQuery.data]);
  const campaignsCountQuery = useCampaignsCountQuery(
    {
      ...modifyParams(page, urlParams.workspaceId as string),
      searchQuery: debouncedSearchQuery
    },
    {
      keepPreviousData: page === prevPage.current,
      enabled: !hasTourToCreateCampaign
    }
  );
  if (prevPage.current !== page) {
    prevPage.current = page;
  }

  const campaignsCount = campaignsCountQuery.data || 0;
  const displayCampaignsCount =
    campaignsCountQuery.data ?? (hasTourToCreateCampaign ? 0 : 10);
  const campaignsToFetch = Math.max(0, campaignsCount - campaignsList.length);

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== urlParams.workspaceId) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'campaign-created'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== urlParams.workspaceId) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'campaign-updated'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== urlParams.workspaceId) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'campaign-removed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    (messages) => {
      const lastMessage = messages[messages.length - 1];
      const { campaignId, reason } = lastMessage.payload;
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      if (
        reason === 'maybe-lost-access' &&
        campaignInfoModal?.campaign?.id === campaignId
      ) {
        dispatch(toggleCampaignInfoModal(null));
      }
    },
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== urlParams.workspaceId) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'campaign-access-changed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== urlParams.workspaceId) return false;
      if (msg.causedByMe && msg.payload.reason !== 'upload') return false;
      return true;
    },
    'asset-created'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== urlParams.workspaceId) return false;
      if (msg.causedByMe) return false;
      return true;
    },
    'asset-removed'
  );

  useWebSocketDebouncedSubscription(
    (msg) => msg.payload.workspaceId,
    () => queryClient.invalidateQueries({ queryKey: ['campaigns', 'list'] }),
    1000,
    (msg) => {
      if (msg.payload.workspaceId !== urlParams.workspaceId) return false;
      if (msg.causedByMe && msg.payload.reason !== 'conversion-status-changed')
        return false;
      return true;
    },
    'asset-updated'
  );

  return (
    <>
      <div className="inner-header">
        <div className="inner-header__name">
          {page === 'deleted' && <DeletedSvg className="inner-header__icon" />}
          <span className="inner-header__title">
            {page.toUpperCase()} PROJECTS
          </span>
          <div className="inner-header__count">{campaignsCountQuery.data}</div>
        </div>
        {page === 'deleted' && (
          <span className="inner-header__description main-body-text main-body-text--dark-gray">
            Projects will be permanently deleted after{' '}
            <span className="main-body-text--main-color">30&nbsp;days</span>
          </span>
        )}
      </div>
      <PageSortPanel
        isActionButtonDisabled={!currentWorkspace?.permissions.createCampaigns}
        actionButtonDisabledReason={
          !currentWorkspace?.permissions.createCampaigns
            ? 'Your current role in StreamWork doesn’t allow you to create a project.'
            : undefined
        }
        entity="project"
        onSearch={setSearchQuery}
        classNameSearchContainer={undefined}
        classNameActionButton={undefined}
        sortMenuModal={
          <Menu
            selectedKeys={[orderBy]}
            onClick={async ({ key: orderBy }) => setOrderBy(orderBy)}
          >
            <span className="help_dropdown__title">Sort by</span>
            <Menu.Item key="order:DESC">
              <span className="help_dropdown__dot" />
              Custom
            </Menu.Item>
            <Menu.Item key="createdAt:DESC">
              <span className="help_dropdown__dot" />
              Date created
            </Menu.Item>
            <Menu.Item key="lastEventTime:DESC">
              <span className="help_dropdown__dot" />
              Date modified
            </Menu.Item>
          </Menu>
        }
        searchInputComponent={undefined}
        classNamePanel={undefined}
        actionButtonName={undefined}
        onAddClick={
          page === 'all'
            ? () =>
                dispatch(
                  toggleCampaignFolderCreationModal({
                    visible: true,
                    entity: 'campaign'
                  })
                )
            : undefined
        }
      />
      <div className="campaign_cards">
        <div className="campaign_cards-content">
          <InfiniteScroll
            loading={campaignsQuery.isFetchingNextPage ? 'more' : false}
            next={campaignsQuery.fetchNextPage}
            hasMore={campaignsQuery.hasNextPage ?? false}
          >
            {displayCampaignsCount === 0 && page === 'all' ? (
              <CampaignsListEmptyState
                canCreateCampaign={
                  currentWorkspace?.permissions.createCampaigns
                }
                hasTourToCreateCampaign={hasTourToCreateCampaign}
              />
            ) : (
              <>
                {displayCampaignsCount === 0 ? (
                  <div className="empty_state empty_state--search">
                    <LottieComponent
                      className="icon"
                      view="noSearchingResult"
                    />

                    <strong
                      className="main-body-text main-body-text--bold"
                      style={{ marginBottom: 8 }}
                    >
                      No results found
                    </strong>
                    <p className="copy main-body-text main-body-text--dark-gray">
                      It seems we can’t find any results based on your search
                    </p>
                  </div>
                ) : (
                  <>
                    {currentView === 'tile' ? (
                      <DraggableList
                        className="campaign_card_list"
                        gap={16}
                        disableReorder={
                          !currentWorkspace?.permissions.changeCampaignsOrder ||
                          page === 'deleted' ||
                          orderBy !== draggableOrder
                        }
                        onReorder={async (e) => {
                          const cancelPromise = queryClient.cancelQueries({
                            queryKey: ['campaigns', 'list']
                          });
                          const beforeCampaignId =
                            optimisticListItemReorder<CampaignItemDto>(
                              {
                                queryKey: ['campaigns', 'list'],
                                id: e.item.id,
                                predicate(query) {
                                  const listParams = query
                                    .queryKey[2] as CampaignControllerListCampaingsParams;
                                  const customOrder = listParams.orderBy?.some(
                                    (x: any) => x.startsWith('order:')
                                  );
                                  return !!customOrder;
                                }
                              },
                              e.prevIndex,
                              e.newIndex
                            );
                          await cancelPromise;
                          if (beforeCampaignId !== false) {
                            await changeCampaignOrder({
                              id: e.item.id,
                              beforeCampaignId
                            });
                          }
                          queryClient.invalidateQueries({
                            queryKey: ['campaigns', 'list']
                          });
                        }}
                      >
                        {!isLoading
                          ? campaignsList
                              .concat(
                                new Array(
                                  campaignsQuery.isFetchingNextPage
                                    ? campaignsToFetch
                                    : 0
                                ).fill('')
                              )
                              .map(({ node }, i) => {
                                if (!node) {
                                  return (
                                    <CampaignsListSkeleton
                                      key={`skeleton-${i}`}
                                    />
                                  );
                                }
                                return (
                                  <DraggableItem
                                    key={node.id}
                                    id={node.id}
                                    type="campaign"
                                  >
                                    <CampaignCard campaign={node} page={page} />
                                  </DraggableItem>
                                );
                              })
                          : new Array(displayCampaignsCount)
                              .fill('')
                              .map((el, i) => (
                                <CampaignsListSkeleton key={`skeleton-${i}`} />
                              ))}
                      </DraggableList>
                    ) : (
                      <CampaignsTable
                        list={campaignsList}
                        countToFetch={
                          campaignsQuery.isFetchingNextPage
                            ? campaignsToFetch
                            : 0
                        }
                        countToLoad={
                          new Array(displayCampaignsCount).fill('') || []
                        }
                        page={page}
                        listLoading={isLoading}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </InfiniteScroll>
        </div>
      </div>
    </>
  );
}

function CampaignsList(props: any) {
  const { workspaceId: workspaceIdParam } = useParams();
  const defaultWorkspace = useDefaultWorkspace(false);
  const [currentWorkspace, setCurrentWorkspace] = useCurrentWorkspace(false);
  const workspaceId =
    workspaceIdParam ?? currentWorkspace?.id ?? defaultWorkspace?.id;
  useEffect(() => {
    if (workspaceId) setCurrentWorkspace(workspaceId);
  }, [workspaceId, setCurrentWorkspace]);
  const needRedirect = !workspaceId || workspaceId !== workspaceIdParam;
  if (needRedirect) {
    return <Navigate to={workspaceId ?? '/dashboard'} replace />;
  }
  return currentWorkspace ? <CampaignsListInWorkspace {...props} /> : null;
}

export default CampaignsList;
